if (!customElements.get('expandable-description-o2')) {
  customElements.define(
    'expandable-description-o2',
    class ExpandableDescriptionO2 extends HTMLElement {
      constructor() {
        super();
        this.handleToggle = this.handleToggle.bind(this);
        this.queueSync = this.queueSync.bind(this);
        this.sync = this.sync.bind(this);
      }

      connectedCallback() {
        this.measure = this.querySelector('[data-expandable-description-measure]');
        this.content = this.querySelector('[data-expandable-description-content]');
        this.toggle = this.querySelector('[data-expandable-description-toggle]');

        if (!this.measure || !this.content || !this.toggle) return;

        this.fullText = this.content.textContent.trim().replace(/\s+/g, ' ');
        this.showMoreLabel = this.dataset.showMoreLabel || 'Show more';
        this.showLessLabel = this.dataset.showLessLabel || 'Show less';

        this.toggle.addEventListener('click', this.handleToggle);
        this.resizeObserver = new ResizeObserver(this.queueSync);
        this.resizeObserver.observe(this);
        this.queueSync();
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.handleToggle);
        this.resizeObserver?.disconnect();
        if (this.syncFrame) cancelAnimationFrame(this.syncFrame);
      }

      queueSync() {
        if (this.syncFrame) return;
        this.syncFrame = requestAnimationFrame(this.sync);
      }

      sync() {
        this.syncFrame = null;
        const expanded = this.classList.contains('is-expanded');

        this.content.textContent = this.fullText;
        this.toggle.textContent = `... ${this.showMoreLabel}`;
        this.toggle.hidden = true;
        this.classList.remove('is-expanded');

        if (!this.isOverflowing()) {
          this.classList.remove('is-expanded');
          this.content.textContent = this.fullText;
          this.toggle.hidden = true;
          this.toggle.setAttribute('aria-expanded', 'false');
          return;
        }

        this.toggle.hidden = false;
        if (expanded) {
          this.expand();
        } else {
          this.collapse();
        }
      }

      handleToggle() {
        if (this.classList.contains('is-expanded')) {
          this.collapse();
        } else {
          this.expand();
        }
      }

      expand() {
        this.content.textContent = `${this.fullText} `;
        this.toggle.textContent = this.showLessLabel;
        this.toggle.hidden = false;
        this.classList.add('is-expanded');
        this.toggle.setAttribute('aria-expanded', 'true');
      }

      collapse() {
        this.toggle.textContent = `... ${this.showMoreLabel}`;
        this.toggle.hidden = false;
        this.classList.remove('is-expanded');

        let low = 0;
        let high = this.fullText.length;
        let best = '';

        while (low <= high) {
          const mid = Math.floor((low + high) / 2);
          const candidate = this.truncateAtWord(mid);
          this.content.textContent = candidate ? `${candidate} ` : '';

          if (this.isWithinLimit()) {
            best = candidate;
            low = mid + 1;
          } else {
            high = mid - 1;
          }
        }

        this.content.textContent = best ? `${best} ` : '';
        this.toggle.setAttribute('aria-expanded', 'false');
      }

      isOverflowing() {
        return this.lineCount(this.content) > 3;
      }

      isWithinLimit() {
        return this.inlineLineCount() <= 3;
      }

      lineCount(node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const tops = [...range.getClientRects()].map((rect) => Math.round(rect.top));

        return this.countLines(tops);
      }

      inlineLineCount() {
        const range = document.createRange();
        range.selectNodeContents(this.content);
        const tops = [...range.getClientRects()].map((rect) => Math.round(rect.top));

        if (!this.toggle.hidden) {
          tops.push(Math.round(this.toggle.getBoundingClientRect().top));
        }

        return this.countLines(tops);
      }

      countLines(tops) {
        return tops
          .sort((a, b) => a - b)
          .reduce((count, top, index, sortedTops) => count + (index === 0 || top - sortedTops[index - 1] > 4 ? 1 : 0), 0);
      }

      truncateAtWord(length) {
        let text = this.fullText.slice(0, length).trimEnd();
        const lastSpace = text.lastIndexOf(' ');

        if (length < this.fullText.length && lastSpace > 0) {
          text = text.slice(0, lastSpace);
        }

        return text.replace(/[,.!?;:]+$/, '');
      }
    }
  );
}
